/**
 * How many write requests are in flight right now, app-wide.
 *
 * Deliberately not marked "use client": it is a plain counter with no React in
 * it, and apiFetch is imported from both environments. On the server the count
 * is simply never read.
 *
 * Exists so the busy overlay is something a call site GETS rather than
 * something it has to remember to add. `apiFetch` bumps this around every
 * non-GET request, `NavigationProgress` renders from it, and no component in
 * between opts in. Before this, every mutation needed its own spinner wiring,
 * so the ones nobody got around to (dragging a card between board columns, for
 * one) felt like dead clicks while a POST was actually running.
 *
 * Reads are deliberately excluded. Background polling (the board's task sync,
 * live agent activity) is all GET, and flashing a modal scrim every five
 * seconds would be far worse than the problem this solves.
 */

const listeners = new Set<(count: number) => void>();
let pending = 0;

function emit() {
  for (const listener of listeners) listener(pending);
}

export function beginMutation(): void {
  pending += 1;
  emit();
}

export function endMutation(): void {
  // Floor at zero: a stray extra end (a double-resolve, a hot reload mid-flight)
  // must not drive the count negative and wedge the overlay off for every later
  // mutation.
  pending = Math.max(0, pending - 1);
  emit();
}

export function pendingMutationCount(): number {
  return pending;
}

/** Subscribe to the count; returns the unsubscribe. */
export function subscribeToMutations(listener: (count: number) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
